import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { IconLeft, IconDelete } from "../../../../../assets/icons";
import { Button, Form, Input, message, Spin, Switch } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { CheckOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { v4 as uuid } from "uuid";
import api from "../../../../../api/api";
import { localStorage } from "@utils";

const RootContainer = styled.div`
  position: relative;
  height: 100%;

  .ant-form {
    .buttons-wrapper {
      position: absolute;
      top: -70px;
      right: 12px;
    }

    .ant-btn {
      height: 48px;
      width: 172px;
    }
  }
`;

const Container = styled.div`
  margin-top: 24px;
  height: 100%;
  border-radius: 8px;
  padding: 24px;
  background: ${(props) => props.theme.backgroundColor};
`;

const Topbar = styled.div`
  padding-top: 16px;
  padding-bottom: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;

  .ant-btn-default {
    height: 48px;
    padding: 0 12px !important;
    border: none !important;
    background: ${(props) => props.theme.backgroundColor} !important;
    color: ${(props) => props.theme.primaryColor} !important;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const TitleWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: #1d1014;
  line-height: 1;
`;

const GrayTitle = styled(Title)`
  color: #868686;
`;

const TitleBar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  ${Title} {
    flex: 1;
  }

  .ant-btn-primary {
    width: 172px;
    height: 48px;
  }
`;

const ItemCard = styled.div`
  .ant-form {
    width: 100%;
    border-radius: 8px;
    background: #fff;
    padding: 24px 16px;
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;

    .ant-input {
      width: 100%;
    }

    .ant-form-item {
      margin-bottom: 0;

      :nth-child(2),
      :nth-child(3) {
        width: 100%;
      }

      :first-child {
        width: 90px;

        * {
          width: 90px;
        }
      }
    }
  }

  .icon-delete {
    width: 48px;
    min-width: 48px;
    height: 48px;
    cursor: pointer;
  }
`;

const SpinWrapper = styled.div`
  height: 80%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Item = ({ index, item, handleUpdateClicked, handleDeleteClicked }) => {
  const [form] = Form.useForm();

  const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;

  useEffect(() => {
    form.setFieldValue("is_active", item.is_active);
    form.setFieldValue("primary", item.primary);
    form.setFieldValue("secondary", item.secondary);

    // eslint-disable-next-line
  }, [item]);

  const onFieldsChange = () => {
    handleUpdateClicked({
      id: item.id,
      is_new: item.is_new,
      ...form.getFieldsValue(),
    });
  };

  return (
    <ItemCard>
      <Form
        form={form}
        initialValues={{
          is_active: item.is_active,
          primary: item.primary,
          secondary: item.secondary,
        }}
        onFieldsChange={onFieldsChange}
      >
        <Form.Item>
          <Input value={index + 1} />
        </Form.Item>
        <Form.Item name={"primary"}>
          <Input placeholder={`Region Name (${storeLang[0]?.language})`} />
        </Form.Item>
        {langCount === 2 &&
        <Form.Item name={"secondary"}>
          <Input placeholder={`Region Name (${storeLang[1]?.language})`} />
        </Form.Item>
        }
        <Form.Item name={"is_active"} valuePropName="checked">
          <Switch />
        </Form.Item>
        <IconDelete
          className="icon-delete"
          onClick={() => handleDeleteClicked(item)}
        />
      </Form>
    </ItemCard>
  );
};

const EditRegion = () => {
  const { city_id, city_name, country_name } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [disabledSave, setDisabledSave] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const [created, setCreated] = useState(false);
  const [edited, setEdited] = useState(false);
  const [editedCount, setEditedCount] = useState(0);
  const [deleted, setDeleted] = useState(false);
  const [deletedCount, setDeletedCount] = useState(0);
  const [itemsToDelete, setItemsToDelete] = useState([]);

  const storeLang = localStorage.getStoreLang() || [];
  const langCount = storeLang?.length;

  useEffect(() => {
    getCityById(city_id);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    let _isEmpty = data.find((x) => isEmpty(x.primary) || (langCount === 2 && isEmpty(x.secondary)));
    setDisabledSave(_isEmpty || data.length === 0);

    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (
      editedCount !== 0 &&
      data.filter((x) => !x.is_new).length === editedCount
    ) {
      setEdited(true);
    }

    // eslint-disable-next-line
  }, [editedCount]);

  useEffect(() => {
    if (deletedCount !== 0 && itemsToDelete.length === deletedCount) {
      setDeleted(true);
    }

    // eslint-disable-next-line
  }, [deletedCount]);

  useEffect(() => {
    if (edited && created && deleted) {
      message.success("Saved the regions successfully!");
      goBack();
    }

    // eslint-disable-next-line
  }, [edited, created, deleted]);

  const getCityById = (city_id) => {
    api
      .getCityById(city_id)
      .then(({ data }) => {
        setLoading(false);
        setData([
          ...data.Townships.map((x) => {
            return {
              is_active: x.is_active,
              primary: x.name?.primary,
              secondary: langCount === 2 ? x.name?.secondary : null,
              id: x.id,
              is_new: false,
            };
          }),
        ]);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const isEmpty = (value) => {
    return !value || value?.trim() === "";
  };

  const addRegions = (values) => {
    if (values.length === 0) {
      setCreated(true);
      return;
    }
    let payload = {
      city_id,
      townships: values.map(({ is_active, primary, secondary }) => {
        return {
          is_active: is_active ? true : false,
          name: {
            primary,
            secondary,
          },
        };
      }),
    };
    api
      .createTownships(payload)
      .then(() => {
        setCreated(true);
      })
      .catch((error) => {
        setConfirmLoading(false);
        message.error(error);
      });
  };

  const editRegions = (values) => {
    if (values.length === 0) {
      setEdited(true);
    }
    values.forEach(({ id, is_active, primary, secondary }) => {
      let payload = {
        id,
        is_active: is_active ? true : false,
        name: {
          primary,
          secondary,
        },
      };
      api
        .editTownship(payload)
        .then(() => {
          setEditedCount((prev) => prev + 1);
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error);
        });
    });
  };

  const deleteRegions = (values) => {
    if (values.length === 0) {
      setDeleted(true);
    }
    values.forEach(({ id }) => {
      api
        .deleteTownship(id)
        .then(() => {
          setDeletedCount((prev) => prev + 1);
        })
        .catch((error) => {
          setConfirmLoading(false);
          message.error(error);
        });
    });
  };

  const handleSave = () => {
    if (!confirmLoading) {
      setConfirmLoading(true);
      let news = data.filter((x) => x.is_new);
      let olds = data.filter((x) => !x.is_new);
      addRegions(news);
      editRegions(olds);
      deleteRegions(itemsToDelete);
    }
  };

  const handleAddCityClicked = () => {
    setData((prev) => {
      return [
        ...prev,
        {
          id: uuid(),
          primary: "",
          secondary: "",
          is_active: true,
          is_new: true,
        },
      ];
    });
  };

  const handleUpdateClicked = (item) => {
    setData((prev) =>
      prev.map((x) => {
        if (x.id === item.id) {
          return item;
        }
        return x;
      })
    );
  };

  const handleDeleteClicked = (item) => {
    !item.is_new && setItemsToDelete((prev) => [...prev, item]);
    setData((prev) => prev.filter((x) => x.id !== item.id));
  };

  const goBack = () => {
    navigate(-1);
  };

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  return (
    <RootContainer>
      <Topbar>
        <Button type="default" icon={<IconLeft />} onClick={goBack}>
          Back
        </Button>
        <TitleWrapper>
          <GrayTitle>Delivery Area</GrayTitle>
          <GrayTitle>{`>`}</GrayTitle>
          <Title>Edit Region</Title>
        </TitleWrapper>
        <Button
          type="primary"
          disabled={disabledSave}
          icon={<CheckOutlined />}
          onClick={handleSave}
          loading={confirmLoading}
        >
          Save
        </Button>
      </Topbar>
      {loading ? (
        <Loader />
      ) : (
        <>
          <TitleBar>
            <Title>{`Add Region in ${city_name}, ${country_name}`}</Title>
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              onClick={handleAddCityClicked}
            >
              Add Region
            </Button>
          </TitleBar>
          <Container>
            {data.map((item, key) => (
              <Item
                key={key}
                index={key}
                item={item}
                handleUpdateClicked={handleUpdateClicked}
                handleDeleteClicked={handleDeleteClicked}
              />
            ))}
          </Container>
        </>
      )}
    </RootContainer>
  );
};

export default EditRegion;
